/**
 * Config-driven pricing for Cendien service lines (Infor, Workday, IT staffing,
 * AI, development). Each line defines its calculator inputs, an estimate
 * function, packages, an à la carte catalog and FAQs; the shared components and
 * pages render everything from this config.
 *
 * Every number here is a planning estimate drawn from Cendien's past bids and
 * service model, shown with PRICING_FINE_PRINT. Change numbers only in the
 * line's config file.
 */

export type ServiceLineId = 'infor' | 'workday' | 'staffing' | 'ai' | 'dev';

export type CalcValue = number | string | boolean | string[];
export type CalcValues = Record<string, CalcValue>;

type InputBase = { id: string; label: string; hint?: string; /** Show only when this returns true. */ showIf?: (v: CalcValues) => boolean };
export type CalcInputDef =
  | (InputBase & { kind: 'slider'; min: number; max: number; step?: number; inputMax?: number; suffix?: string })
  | (InputBase & { kind: 'choice'; options: { value: string; label: string; sub?: string }[]; columns?: 2 | 3 })
  | (InputBase & { kind: 'toggle' })
  | (InputBase & { kind: 'chips'; options: string[] });

export interface EstimateResult {
  low: number;
  high: number;
  /** Typical figure shown large. */
  mid: number;
  /** e.g. "per 4-week period", "per month", "one-time". */
  unitLabel: string;
  /** Line items for the bars (amounts in the same unit as mid). */
  breakdown: { label: string; amount: number }[];
  /** One-line facts under the total (effective rate, annual cost, hours). */
  facts: string[];
  /** Plain-text summary for copy and lead context. */
  summary: string;
  /** Optional callout under the bars. */
  note?: string;
}

export interface ServicePlan {
  id: string;
  name: string;
  blurb: string;
  price: string;
  unit: string;
  items: string[];
  feature?: boolean;
  icon: string; // file under /images/icons/services
  bestFor: string;
  /** Calculator values this plan sets when chosen. */
  calcPreset?: CalcValues;
  /** Numeric bounds for schema.org offers (USD). */
  schemaPrice?: { min: number; max: number; unitText: string };
}

export interface CatalogGroup {
  id: string;
  group: string;
  intro: string;
  items: { id: string; name: string; desc: string; price: string; unit: string }[];
}

export interface ServiceLine {
  id: ServiceLineId;
  /** "Infor managed services" */
  name: string;
  /** Short label used in chips and breadcrumbs. */
  short: string;
  /** Value sent to the service-quote form's "service" field. */
  formService: string;
  parentPath: string;
  parentLabel: string;
  pricingPath: string;
  landingPrefix: string;
  pricingTitle: string; // H1
  pricingIntro: string;
  heroBullets: string[];
  calcIntro: string;
  inputs: CalcInputDef[];
  defaults: CalcValues;
  estimate: (v: CalcValues) => EstimateResult;
  plansTitle: string;
  plansIntro: string;
  plans: ServicePlan[];
  catalogTitle: string;
  catalog: CatalogGroup[];
  answerTitle: string;
  answer: string;
  answerBullets: string[];
  drivers: { icon: string; title: string; body: string }[];
  faqs: { q: string; a: string }[];
  related: { label: string; to: string; description: string; icon: string }[];
  explainerVideoKey?: import('@/data/explainerVideos').ExplainerVideoKey;
  /** Amount at which a breakdown bar fills 100% (square-root scale), so bars grow and shrink with the inputs. */
  barRef: number;
  /** Three example scenarios built around a landing page's preset (small, typical, large). */
  examples: (base: CalcValues) => { label: string; values: CalcValues }[];
  /** Side-by-side comparison with the usual alternatives. */
  compare: { title: string; columns: string[]; rows: string[][]; note?: string };
}

export const money = (n: number) => '$' + Math.round(n).toLocaleString('en-US');
export const num = (v: CalcValue | undefined, d = 0) => (typeof v === 'number' && Number.isFinite(v) ? v : d);
export const str = (v: CalcValue | undefined, d = '') => (typeof v === 'string' ? v : d);
export const bool = (v: CalcValue | undefined) => v === true;
export const list = (v: CalcValue | undefined) => (Array.isArray(v) ? v : []);

/** Lower-case a service name mid-sentence while keeping brands and acronyms (Infor, Workday, IT, AI). */
export const midSentence = (name: string) => (/^(Infor|Workday|IT|AI|Microsoft)\b/.test(name) ? name : name.charAt(0).toLowerCase() + name.slice(1));
